import {
  Box,
  Flex,
  rem,
  rgba,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBrandWhatsapp,
  IconClock24,
  IconRobot,
  IconUserPlus,
} from "@tabler/icons-react";

/**
 * Descrição do passo 1 sem a promessa de "sem cartão": no link de pagamento
 * direto o cartão É pedido, logo depois do wizard. A frase original é verdadeira
 * ao pé da letra (o cadastro em si não pede cartão), mas quem lê rápido entende
 * "não vou precisar de cartão" — e vai precisar em três minutos.
 */
const SIGNUP_STEP_WITHOUT_TRIAL = "Cadastro rápido, em menos de um minuto.";

const STEPS = [
  {
    Icon: IconUserPlus,
    title: "Crie sua conta",
    description: "Cadastro rápido, sem cartão de crédito.",
  },
  {
    Icon: IconRobot,
    title: "Personalize seu agente",
    description: "Treine a IA com o tom e as regras do seu negócio.",
  },
  {
    Icon: IconBrandWhatsapp,
    title: "Conecte ao WhatsApp",
    description: "Escaneie um QR Code e está pronto para receber clientes.",
  },
  {
    Icon: IconClock24,
    title: "Atendimento 24/7",
    description: "Seu agente responde e qualifica leads enquanto você dorme.",
  },
];

type SignUpHeroPanelProps = {
  /**
   * SA-1218: o selo de teste grátis só aparece quando o link dá trial. Num link
   * de vendas "pagamento direto" a cobrança sai no ato, então prometer dias
   * grátis aqui, na primeira coisa que o lead lê, seria cobrança surpresa.
   */
  showTrialCopy?: boolean;
  /**
   * SA-1279: claim do cupom do `?promo=` da URL, quando o servidor confirmou o
   * código. Independente do trial: um link de pagamento direto com cupom mostra
   * o desconto e continua sem prometer dias grátis.
   */
  promoHeadline?: string | null;
};

export const SignUpHeroPanel = ({
  showTrialCopy = true,
  promoHeadline = null,
}: SignUpHeroPanelProps) => {
  const theme = useMantineTheme();

  const gradient = `linear-gradient(160deg, ${theme.colors["R500"][0]} 0%, ${theme.colors["R700"][0]} 100%)`;

  const steps = showTrialCopy
    ? STEPS
    : STEPS.map((step, idx) =>
        idx === 0 ? { ...step, description: SIGNUP_STEP_WITHOUT_TRIAL } : step
      );

  return (
    <Flex
      direction="column"
      justify="space-between"
      h="100%"
      w="100%"
      p={rem(40)}
      style={{
        background: gradient,
        color: "white",
        position: "relative",
        overflow: "hidden",
      }}>
      <Stack gap={rem(28)}>
        <Stack gap={rem(8)}>
          <Title
            order={2}
            ff="heading"
            fz={rem(36)}
            fw={800}
            lh={1.1}
            c="white">
            Seu atendimento no piloto automático
          </Title>
          <Text
            fz={rem(16)}
            lh={1.5}
            c={rgba("#FFFFFF", 0.85)}>
            Crie um agente de IA treinado para o seu negócio e comece a atender
            no WhatsApp em poucos minutos.
          </Text>
        </Stack>

        <Stack gap={rem(12)}>
          {steps.map(({ Icon, title, description }, idx) => (
            <Flex
              key={title}
              gap={rem(14)}
              align="flex-start"
              p={rem(14)}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: rem(12),
                backdropFilter: "blur(4px)",
              }}>
              <Flex
                w={rem(36)}
                h={rem(36)}
                align="center"
                justify="center"
                style={{
                  flexShrink: 0,
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: rem(8),
                }}>
                <Icon
                  size={20}
                  stroke={1.75}
                  color="white"
                />
              </Flex>
              <Stack gap={rem(2)}>
                <Text
                  fz={rem(11)}
                  fw={700}
                  c={rgba("#FFFFFF", 0.6)}
                  style={{ letterSpacing: "0.5px" }}>
                  PASSO {idx + 1}
                </Text>
                <Text
                  fz={rem(15)}
                  fw={700}
                  ff="heading"
                  c="white"
                  lh={1.2}>
                  {title}
                </Text>
                <Text
                  fz={rem(13)}
                  c={rgba("#FFFFFF", 0.75)}
                  lh={1.4}>
                  {description}
                </Text>
              </Stack>
            </Flex>
          ))}
        </Stack>
      </Stack>

      {(promoHeadline || showTrialCopy) && (
        <Stack
          gap={rem(12)}
          mt={rem(24)}>
          {promoHeadline && (
            <Box
              px={rem(14)}
              py={rem(8)}
              w="fit-content"
              style={{
                background: "white",
                color: theme.colors["R700"][0],
                borderRadius: rem(999),
                fontWeight: 800,
                fontSize: rem(13),
                letterSpacing: "0.3px",
              }}>
              🏷️ {promoHeadline}
            </Box>
          )}
          {showTrialCopy && (
            <Flex
              align="center"
              gap={rem(12)}>
              <Box
                px={rem(14)}
                py={rem(8)}
                style={{
                  background: promoHeadline
                    ? "rgba(255,255,255,0.15)"
                    : "white",
                  color: promoHeadline ? "white" : theme.colors["R700"][0],
                  borderRadius: rem(999),
                  fontWeight: 800,
                  fontSize: rem(13),
                  letterSpacing: "0.3px",
                }}>
                🎁 7 dias grátis
              </Box>
              <Text
                fz={rem(13)}
                c={rgba("#FFFFFF", 0.8)}>
                Sem cartão de crédito · cancele quando quiser
              </Text>
            </Flex>
          )}
        </Stack>
      )}
    </Flex>
  );
};

export default SignUpHeroPanel;
